import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Select } from 'antd';

import { PopupNames } from 'core';
import { usePaginatedGroupsQuery } from 'core/graphql';
import {
  usePopupData,
  usePopupStateOperations,
  useCareerDirections,
  useCareerGroupRoster,
  useCareerMemberMutations,
  IEnrollResult,
} from 'core/hooks';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

const GROUP_SEARCH_DEBOUNCE_MS = 300;

export interface IEnrollCareerMembersPopupData {
  /** Fired after a successful enrolment so the caller can refetch + toast. */
  onEnrolled?: (result: IEnrollResult) => void;
}

/**
 * Spec 0018 — «Добавить участников».
 *
 * Students and a group are additive, not exclusive: the backend unions them,
 * so an operator can enrol a whole cohort plus a couple of stragglers in one
 * pass. Re-running is safe — an already-enrolled student comes back under
 * `skipped`, an archived one under `reactivated`. That breakdown is handed to
 * the caller, which owns the success toast.
 *
 * No `<Modal>` here: `GlobalModal` already portals one around every popup, and
 * a second would stack two fixed backdrops.
 */
export const EnrollCareerMembersPopup = memo(() => {
  const { t } = useTranslation();
  const data = usePopupData(PopupNames.enrollCareerMembersPopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [rosterSearch, setRosterSearch] = useState('');
  const [debouncedRosterSearch, setDebouncedRosterSearch] = useState('');
  const [groupId, setGroupId] = useState<string | undefined>();
  const [directionId, setDirectionId] = useState<string | undefined>();
  const [groupSearch, setGroupSearch] = useState('');
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
  const [formError, setFormError] = useState<string | undefined>();

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedRosterSearch(rosterSearch.trim()),
      GROUP_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [rosterSearch]);

  const { rows: roster, loading: rosterLoading } = useCareerGroupRoster(
    groupId,
    debouncedRosterSearch,
  );

  const { directions } = useCareerDirections();
  const { enroll, pending } = useCareerMemberMutations();

  const close = useCallback(() => {
    hidePopup(PopupNames.enrollCareerMembersPopup);
  }, [hidePopup]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedGroupSearch(groupSearch.trim()),
      GROUP_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [groupSearch]);

  const { data: groupsData, loading: groupsLoading } = usePaginatedGroupsQuery({
    // Список групп для выбора — только id и имя: закрытые ролями поля
    // (`price`, `currency` — `GROUP_STAFF_FIELDS_ROLES`) не нужны и не
    // запрашиваются, иначе методисту приходил отказ поля.
    variables: {
      query: debouncedGroupSearch || undefined,
      first: 20,
      withStaffFields: false,
    },
    fetchPolicy: 'cache-and-network',
  });

  const groupOptions = useMemo(
    () =>
      (groupsData?.allGroups?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({ value: node.id, label: node.name })),
    [groupsData],
  );

  const directionOptions = useMemo(
    () =>
      directions.map((direction) => ({
        value: direction.id,
        label: direction.title,
      })),
    [directions],
  );

  // 🔴 Сид «выбрать всех» ровно ОДИН раз на смену источника, через ref.
  // `cache-and-network` подсовывает новую идентичность массива при фоновом
  // рефетче, и наивный `useEffect` затирал бы ручные снятия галочек — на
  // соседнем экране это уже стоило повторной отправки писем.
  const seededRef = useRef<string | null>(null);
  const sourceKey = `${groupId || ''}|${debouncedRosterSearch}`;

  useEffect(() => {
    if (seededRef.current === sourceKey) return;
    seededRef.current = sourceKey;
    setPicked(new Set());
  }, [sourceKey]);

  const selectable = useMemo(
    () => roster.filter((row) => row.memberStatus !== 'active'),
    [roster],
  );
  const allPicked =
    selectable.length > 0 &&
    selectable.every((row) => picked.has(row.studentId));

  const toggleAll = useCallback(() => {
    setPicked((prev) => {
      const next = new Set(prev);
      const every = selectable.every((row) => next.has(row.studentId));
      selectable.forEach((row) => {
        if (every) next.delete(row.studentId);
        else next.add(row.studentId);
      });
      return next;
    });
  }, [selectable]);

  const togglePick = useCallback((studentId: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }, []);

  const onSubmit = useCallback(async () => {
    setFormError(undefined);
    // Явный выбор — это ВЕСЬ выбор: бэкенд разворачивает группу в ростер
    // только когда никого не отметили. Группу шлём всё равно — она остаётся
    // пометкой «откуда пришёл» (`source_group`).
    const studentIds = Array.from(picked);
    // Guard the no-op submit: with neither target the backend enrols nobody
    // and still answers `success`, which reads to the operator as a silent
    // failure.
    if (!studentIds.length && !groupId) {
      setFormError(t('pages.careerCenter.enrollPopup.errorNoTarget'));
      return;
    }
    const result = await enroll({ studentIds, groupId, directionId });
    if (result) {
      data?.onEnrolled?.(result);
      close();
    }
  }, [picked, groupId, directionId, enroll, data, close, t]);

  return (
    <PopupContainer
      width="560px"
      title={t('pages.careerCenter.enrollPopup.title')}
      onClose={close}
    >
      <FormBody>
        <Hint>{t('pages.careerCenter.enrollPopup.hint')}</Hint>

        <Field>
          <FieldLabel>{t('pages.careerCenter.enrollPopup.group')}</FieldLabel>
          <Select
            showSearch
            allowClear
            style={{ width: '100%' }}
            value={groupId}
            loading={groupsLoading}
            filterOption={false}
            onSearch={setGroupSearch}
            onChange={(value: string | undefined) => setGroupId(value)}
            placeholder={t('pages.careerCenter.enrollPopup.groupPlaceholder')}
            options={groupOptions}
          />
        </Field>

        <Field>
          <FieldLabel>
            {t('pages.careerCenter.enrollPopup.students')}
          </FieldLabel>
          {/* Поиск по ФИО во всей базе — для выпускников старых групп и
              переведённых, которых в выбранной группе уже нет. */}
          <SearchInput
            value={rosterSearch}
            onChange={(event) => setRosterSearch(event.target.value)}
            placeholder={t('pages.careerCenter.enrollPopup.searchPlaceholder')}
          />
          {roster.length > 0 && (
            <RosterHead>
              <SelectAllLabel htmlFor="roster-select-all">
                <input
                  id="roster-select-all"
                  type="checkbox"
                  checked={allPicked}
                  onChange={toggleAll}
                />{' '}
                {t('pages.careerCenter.enrollPopup.selectAll')}
              </SelectAllLabel>
              <span>
                {t('pages.careerCenter.enrollPopup.picked', {
                  n: picked.size,
                })}
              </span>
            </RosterHead>
          )}
          <RosterList>
            {rosterLoading && !roster.length ? (
              <RosterEmpty>
                {t('pages.careerCenter.enrollPopup.loading')}
              </RosterEmpty>
            ) : null}
            {!rosterLoading && !roster.length ? (
              <RosterEmpty>
                {t('pages.careerCenter.enrollPopup.noStudents')}
              </RosterEmpty>
            ) : null}
            {roster.map((row) => {
              // Уже состоящий в центре недоступен для выбора и помечен: без
              // этого оператор узнавал правду только из `skipped` в тосте.
              const already = row.memberStatus === 'active';
              return (
                <RosterRow key={row.studentId} muted={already}>
                  <input
                    type="checkbox"
                    disabled={already}
                    checked={picked.has(row.studentId)}
                    onChange={() => togglePick(row.studentId)}
                  />
                  <RosterName>
                    {row.fullName}
                    {row.email ? <RosterMeta> · {row.email}</RosterMeta> : null}
                  </RosterName>
                  {row.memberStatus ? (
                    <RosterBadge>
                      {t(
                        already
                          ? 'pages.careerCenter.enrollPopup.alreadyIn'
                          : 'pages.careerCenter.enrollPopup.willReturn',
                      )}
                    </RosterBadge>
                  ) : null}
                </RosterRow>
              );
            })}
          </RosterList>
        </Field>

        <Field>
          <FieldLabel>
            {t('pages.careerCenter.enrollPopup.direction')}
          </FieldLabel>
          <Select
            allowClear
            style={{ width: '100%' }}
            value={directionId}
            onChange={(value: string | undefined) => setDirectionId(value)}
            placeholder={t(
              'pages.careerCenter.enrollPopup.directionPlaceholder',
            )}
            options={directionOptions}
          />
        </Field>

        {formError ? <FormError>{formError}</FormError> : null}

        <FormFooter>
          <ActionButton type="button" onClick={close} disabled={pending}>
            {t('pages.careerCenter.enrollPopup.cancel')}
          </ActionButton>
          <ActionButton
            primary
            type="button"
            onClick={onSubmit}
            disabled={pending}
          >
            {pending
              ? t('pages.careerCenter.enrollPopup.submitting')
              : t('pages.careerCenter.enrollPopup.submit')}
          </ActionButton>
        </FormFooter>
      </FormBody>
    </PopupContainer>
  );
});

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
`;

const Hint = styled.div`
  font-size: 13px;
  color: #667085;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #667085;
`;

const FormError = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Red};
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  border: 1px solid ${({ primary }) => (primary ? '#2e90fa' : '#e4e7ec')};
  background: ${({ primary }) => (primary ? '#2e90fa' : Colors.White)};
  color: ${({ primary }) => (primary ? Colors.White : '#344054')};
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
`;

const SelectAllLabel = styled.label`
  cursor: pointer;
`;

const RosterHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 4px;
  font-size: 12px;
  color: #667085;
`;

/* Высота фиксирована: состав группы бывает и на тридцать человек, а диалог не
   должен уезжать за экран вместе с кнопкой «Добавить». */
const RosterList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
`;

const RosterRow = styled.label<{ muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  font-size: 13px;
  cursor: ${({ muted }) => (muted ? 'default' : 'pointer')};
  opacity: ${({ muted }) => (muted ? 0.55 : 1)};

  & + & {
    border-top: 1px solid #f2f4f7;
  }
`;

const RosterName = styled.span`
  flex: 1;
  min-width: 0;
`;

const RosterMeta = styled.span`
  color: #667085;
`;

const RosterBadge = styled.span`
  font-size: 11px;
  color: #667085;
  white-space: nowrap;
`;

const RosterEmpty = styled.div`
  padding: 14px 10px;
  font-size: 13px;
  color: #667085;
  text-align: center;
`;
